/* eslint-disable react/no-unused-prop-types */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Paper, Fab, CircularProgress } from '@material-ui/core';
import { ZoomIn, ZoomOut, CancelPresentation } from '@material-ui/icons';
import styled from 'styled-components';
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import parse from 'html-react-parser';
import Tooltip from '@material-ui/core/Tooltip';
import { findSGZone } from '../../../helpers/pricing';

const styles = makeStyles(theme => ({
  sectionPath: {
    '&:hover': {
      cursor: 'pointer',
      fill: 'rgba(156,39,176, 0.8)',
      stroke: 'black',
      strokeWidth: '10px',
      transition: theme.transitions.create(['all'], {
        duration: theme.transitions.duration.complex,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    '&:focus': {
      cursor: 'pointer',
      fill: 'rgba(156,39,176, 0.8)',
      stroke: 'black',
      strokeWidth: '10px',
      transition: theme.transitions.create(['all'], {
        duration: theme.transitions.duration.complex,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
  },
  margin: {
    margin: '0 5px;',
  },
  fitFix: {
    width: '100%',
    height: '100%',
  },
  venueMapInProcess: {
    width: 300,
    height: 300,
  },
}));

const MapWrapper = styled(Paper)`
  overflow: hidden;
  margin-bottom: 20px;
  background-color: ${props => (props.theme === 'light' ? '#FFF' : '#000')};
  position: relative;
  > :first-child {
    width: 100% !important;
    height: 100% !important;
  }
`;

const TransformIntermediateWrap = styled.div`
  > :last-child {
    width: 100% !important;
    height: 100% !important;
    > div {
      width: 100% !important;
      height: 100% !important;
    }
  }
`;

const MapTools = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1;
`;

// TODO: delete?
const TransformComponentWrap = styled(TransformComponent)`
  > :first-child {
    width: 100% !important;
  }
`;

const LightTooltip = withStyles(() => ({
  tooltip: {
    fontSize: 16,
  },
}))(Tooltip);

function cssEscape(str) {
  return str.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

function VenueMap(props) {
  const classes = styles();
  const theme = useTheme();

  const beforeMapSectionClickCallback = e => {
    const { mapSectionClickCallback, seatingSelectionMode, currCompSections, isCompAll } = props;
    const zoneClicked = e.target.dataset.zone;
    if (seatingSelectionMode === 'zone') {
      const zselected = currCompSections.filter(cs => cs.zone_name === zoneClicked);

      if (zselected.length) {
        let el = '';
        if (!Number.isNaN(Number(zoneClicked[0]))) {
          el = document.querySelectorAll(`#z${cssEscape(zoneClicked?.replace(/\s+/g, ''))}`);
        } else {
          el = document.querySelectorAll(`#${cssEscape(zoneClicked?.replace(/\s+/g, ''))}`);
        }

        if (el) {
          el.forEach(elem => {
            elem.classList.remove('makeStyles-sectionPath-100-hover');
          });
        }
      }
    }
    if (!isCompAll) {
      mapSectionClickCallback(e);
    }
  };

  const getFillStrokeColor = (zoneName, sectionName) => {
    const { currCompSections, isCompAll, activeMarketplace } = props;
    const foundMatch =
      (sectionName &&
        currCompSections.some(
          seating =>
            (activeMarketplace === 'ticketnetwork' &&
              seating.zone_name === zoneName &&
              seating.section_name === sectionName) ||
            (activeMarketplace === 'seatgeek' && seating.section_name.toUpperCase() === sectionName.toUpperCase())
        )) ||
      isCompAll;
    if (foundMatch) {
      return { fillColor: 'rgba(156,39,176, 0.5)', stroke: '#9c27b0', strokeWidth: '10px' };
    }
    return { fillColor: 'rgba(156,39,176, 0.1)', stroke: 'black', strokeWidth: '2px' };
  };

  const removeBreakLines = (str = '') => {
    return str.replace(/\n|\r/g, '');
  };

  const tooltipTitle = (zone, section) => {
    if (zone && section) {
      return `${section} | ${zone}`;
    }
    if (section && !zone) {
      return section;
    }
    return null;
  };

  const getSgPathElements = section => {
    const { zones } = props;
    const sgSections = findSGZone(zones, section).sections.map(sgsec => sgsec.canonical_name);
    const sgElements = new Set();

    sgSections.forEach(sgsec => {
      const className = sgsec?.toLowerCase().replace(/\s+/g, '');
      const elements = !Number.isNaN(Number(sgsec))
        ? document.querySelectorAll(`.z${className}`)
        : document.querySelectorAll(`.${className}`);
      if (elements.length) {
        sgElements.add(elements);
      }
    });
    return sgElements;
  };

  const parsedVenueSvg = () => {
    // eslint-disable-next-line camelcase
    const { venueImages, mapSectionClickCallback, currCompSections, config, activeMarketplace } = props;
    const svg = venueImages[activeMarketplace]?.svg ?? '';
    // eslint-disable-next-line
    const jpg_uri = venueImages[activeMarketplace]?.jpg_uri ?? '';

    if (svg && svg.length > 162) {
      const parsedSvg = parse(removeBreakLines(svg));
      const props2 = parsedSvg.props;

      if (props2 && props2.children) {
        let pathElementsWithClickHandler = {};
        if (props2.children.length) {
          pathElementsWithClickHandler = props2.children.map(pathElement => {
            if (pathElement.props) {
              const section = pathElement.props['data-section'];
              const zone = pathElement.props['data-zone']?.replace(/\s+/g, '') || '';
              const fillStokeData = getFillStrokeColor(
                pathElement.props['data-zone'],
                pathElement.props['data-section']
              );

              const zoneSection = zone + section?.replace(/\s+/g, '');
              if (section && section !== 'stage') {
                return (
                  <LightTooltip
                    title={tooltipTitle(pathElement.props['data-zone'], pathElement.props['data-section'])}
                    placement="top"
                  >
                    {{
                      ...pathElement,
                      props: {
                        ...pathElement.props,
                        onClick: beforeMapSectionClickCallback,
                        style: { pointerEvents: 'auto' },
                        fill: fillStokeData.fillColor,
                        stroke: fillStokeData.stroke,
                        strokeWidth: fillStokeData.strokeWidth,
                        className: !Number.isNaN(Number(zoneSection[0])) ? `z${zoneSection}` : zoneSection,
                        key: pathElement.props['data-zone']
                          ? pathElement.props['data-zone'] + pathElement.props['data-section']
                          : pathElement.props['data-section'],
                        id: !Number.isNaN(Number(zone[0])) ? `z${zone}` : zone,
                        onMouseEnter: () => {
                          try {
                            if (activeMarketplace === 'seatgeek') {
                              const sgElements = getSgPathElements(section);
                              if (sgElements.size) {
                                sgElements.forEach(p => {
                                  p.forEach(s => {
                                    if (
                                      s.className.animVal.includes(`${section.toLowerCase().replace(/\s+/g, '')}`) ||
                                      s.className.animVal.includes(
                                        `${section.toLowerCase().replace(/\s+6067522/g, '')}`
                                      )
                                    ) {
                                      s.classList.add('makeStyles-sectionPath-sg-100-hover');
                                    } else {
                                      s.classList.add('makeStyles-sectionPath-sg-99-hover');
                                    }
                                  });
                                });
                              }
                            } else {
                              let el = '';
                              if (!Number.isNaN(Number(zone[0]))) {
                                el = document.querySelectorAll(`#z${zone?.replace(/\s+/g, '')}`);
                              } else {
                                el = document.querySelectorAll(`#${zone?.replace(/\s+/g, '')}`);
                              }
                              let element = !Number.isNaN(Number(zoneSection[0]))
                                ? document.querySelectorAll(`.z${zoneSection}`)
                                : document.querySelectorAll(`.${zoneSection}`);
                              const sections = currCompSections.map(s => ({
                                zone: s.zone_name,
                                section: s.section_name,
                              }));
                              // eslint-disable-next-line no-restricted-syntax
                              for (const s of sections) {
                                const curr = s.zone?.replace(/\s+/g, '') + s.section?.replace(/\s+/g, '');
                                const currElement = !Number.isNaN(Number(curr[0]))
                                  ? document.querySelectorAll(`.z${curr}`)
                                  : document.querySelectorAll(`.${curr}`);
                                element = [...element, ...currElement];
                              }
                              if (el) {
                                el.forEach(e => {
                                  e.classList.add('makeStyles-sectionPath-99-hover');
                                });
                              }
                              if (element.length) {
                                // eslint-disable-next-line no-restricted-syntax
                                for (const item of element) {
                                  item.classList.add('makeStyles-sectionPath-100-hover');
                                }
                              }
                            }
                          } catch (e) {
                            //
                          }
                        },
                        onMouseLeave: () => {
                          try {
                            if (activeMarketplace === 'seatgeek') {
                              const sgElements = getSgPathElements(section);
                              if (sgElements.size) {
                                sgElements.forEach(p => {
                                  p.forEach(s => {
                                    if (
                                      s.className.animVal.includes(`${section.toLowerCase().replace(/\s+/g, '')}`) ||
                                      s.className.animVal.includes(`${section.toLowerCase().replace(/\s+/g, '')}`)
                                    ) {
                                      s.classList.remove('makeStyles-sectionPath-sg-100-hover');
                                    } else {
                                      s.classList.remove('makeStyles-sectionPath-sg-99-hover');
                                    }
                                  });
                                });
                              }
                            } else {
                              let el = '';
                              if (!Number.isNaN(Number(zone[0]))) {
                                el = document.querySelectorAll(`#z${zone?.replace(/\s+/g, '')}`);
                              } else {
                                el = document.querySelectorAll(`#${zone?.replace(/\s+/g, '')}`);
                              }
                              const element = !Number.isNaN(Number(zoneSection[0]))
                                ? document.querySelectorAll(`.z${zoneSection}`)
                                : document.querySelectorAll(`.${zoneSection}`);
                              if (el) {
                                el.forEach(e => {
                                  e.classList.remove('makeStyles-sectionPath-99-hover');
                                });
                              }
                              if (element.length) {
                                // eslint-disable-next-line no-restricted-syntax
                                for (const item of element) {
                                  item.classList.remove('makeStyles-sectionPath-100-hover');
                                }
                              }
                            }
                          } catch (e) {
                            //
                          }
                        },
                      },
                    }}
                  </LightTooltip>
                );
                // eslint-disable-next-line no-else-return
              } else if (section && section === 'stage') {
                return (
                  <path
                    d={pathElement.props.d}
                    fill="grey"
                    stroke={pathElement.props.stroke}
                    strokeWidth={pathElement.props.strokeWidth}
                    data_section={pathElement.props.data - section}
                    key={pathElement.props.key}
                  >
                    {pathElement.props.children}
                  </path>
                );
                // eslint-disable-next-line no-else-return
              } else if (pathElement.type === 'text') {
                let fontSizeAdjusted = String(parseFloat(pathElement.props.fontSize.replace('em', '')) * 1);
                fontSizeAdjusted += 'em';
                return (
                  <text
                    // x={pathElement.props.x}
                    // y={pathElement.props.y}
                    transform={pathElement.props.transform}
                    alignmentBaseline={pathElement.props.alignmentBaseline}
                    fontSize={fontSizeAdjusted}
                    strokeWidth={pathElement.props.strokeWidth}
                    stroke={pathElement.props.stroke}
                    textAnchor={pathElement.props.textAnchor}
                  >
                    {pathElement.props.children}
                  </text>
                );
              }
              return <></>;
            }
            return <></>;
          });
        } else {
          const pathElement = parsedSvg.props.children;
          const fillStokeData = getFillStrokeColor(pathElement.props['data-zone'], pathElement.props['data-section']);
          const zone = pathElement.props['data-zone']?.replace(/\s+/g, '') || '';
          const section = pathElement.props['data-section'];
          const zoneSection = zone + section?.replace(/\s+/g, '');

          pathElementsWithClickHandler = [
            {
              ...pathElement,
              props: {
                ...pathElement.props,
                onClick: mapSectionClickCallback,
                style: { pointerEvents: 'auto' },
                fill: fillStokeData.fillColor,
                stroke: fillStokeData.stroke,
                strokeWidth: fillStokeData.strokeWidth,
                id: !Number.isNaN(Number(zone[0])) ? `z${zone}` : zone,
                className: !Number.isNaN(Number(zoneSection[0])) ? `z${zoneSection}` : zoneSection,
                key: pathElement.props['data-zone']
                  ? pathElement.props['data-zone'] + pathElement.props['data-section']
                  : pathElement.props['data-section'],
              },
            },
          ];
        }
        if (activeMarketplace === 'ticketnetwork') {
          const imageOverlay = React.createElement('image', {
            x: config.x,
            y: config.y,
            preserveAspectRatio: 'none',
            width: config.width,
            height: config.height,
            xlinkHref: jpg_uri,
            key: jpg_uri,
          });
          pathElementsWithClickHandler.unshift(imageOverlay);
        }
        return React.createElement(
          parsedSvg.type,
          {
            ...parsedSvg.props,
            width: '100%',
            height: '100%',
            style: {
              pointerEvents: 'none',
              filter: theme.palette.type === 'dark' ? 'invert(1) hue-rotate(45deg)' : 'none',
            },
          },
          pathElementsWithClickHandler
        );
      }
    }

    return (
      <div className={classes.venueMapInProcess} data-testid="venue-map-in-progress">
        Venue Map In-progress
      </div>
    );
  };

  const { groupsBasicInfo, groupSelected } = props;

  useEffect(() => {
    const el = document.querySelectorAll(`.makeStyles-sectionPath-100-hover`);

    if (el) {
      el.forEach(elem => {
        elem.classList.remove('makeStyles-sectionPath-100-hover');
      });
    }
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.currCompSections, groupsBasicInfo, groupSelected]);

  return (
    <MapWrapper
      data-testid="seating-venue-map"
      theme={theme.palette.type}
      style={{ filter: groupsBasicInfo && groupSelected ? 'blur(1px)' : 'none' }}
      square
    >
      {groupsBasicInfo && groupSelected && (
        <div
          style={{
            position: 'absolute',
            zIndex: '1000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}
      <TransformWrapper className={classes.fitFix}>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <TransformIntermediateWrap>
            <MapTools>
              <Fab size="small" aria-label="Add" className={classes.margin} onClick={zoomIn}>
                <ZoomIn />
              </Fab>
              <Fab size="small" aria-label="Add" className={classes.margin} onClick={zoomOut}>
                <ZoomOut />
              </Fab>
              <Fab size="small" aria-label="Add" className={classes.margin} onClick={resetTransform}>
                <CancelPresentation />
              </Fab>
            </MapTools>
            <TransformComponentWrap className={classes.fitFix}>{parsedVenueSvg()}</TransformComponentWrap>
          </TransformIntermediateWrap>
        )}
      </TransformWrapper>
    </MapWrapper>
  );
}

VenueMap.propTypes = {
  venueImages: PropTypes.object,
  mapSectionClickCallback: PropTypes.func.isRequired,
  seatingSelectionMode: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  isCompAll: PropTypes.bool.isRequired,
  activeMarketplace: PropTypes.string.isRequired,
  zones: PropTypes.array.isRequired,
  groupsBasicInfo: PropTypes.bool.isRequired,
  groupSelected: PropTypes.bool.isRequired,
  currCompSections: PropTypes.array.isRequired,
};
VenueMap.defaultProps = {
  venueImages: {},
};

function mapStateToProps(state) {
  return {
    groupsBasicInfo: state.refreshGroups.groupsBasicInfo,
    groupSelected: state.pricing.groupSelected,
    zones: state.zones,
    currCompSections: state.pricing.currTNComparisonSections,
    venueImages: state.venues.venueData,
  };
}

export default connect(mapStateToProps)(VenueMap);
